/** Where this canvas fetches a surface module from, and what budget it holds it to. OVL-016. */

import type { Authority, CanvasParams } from "../transport/authority";
import { readParams } from "../transport/authority";
import { apiOriginFrom } from "../transport/pairing";
import type { SurfaceCatalogue } from "./browserPorts";

/** The path the agent serves a module on, matching `SURFACE_PATH` in its own server. */
export function agentSurfacePath(activityId: string, surface: string): string {
  return `/surfaces/${encodeURIComponent(activityId)}/${encodeURIComponent(surface)}`;
}

/** The path the cloud serves the same bytes on, under the canvas router. */
export function cloudSurfacePath(activityId: string, surface: string): string {
  return `/v1/canvas${agentSurfacePath(activityId, surface)}`;
}

export interface SurfaceModuleBase {
  readonly origin: string;
  readonly path: (activityId: string, surface: string) => string;
}

/** Where the authority that answered serves modules, or undefined when it can serve none. */
export function surfaceModuleBase(
  authority: Authority | undefined,
  params: CanvasParams,
): SurfaceModuleBase | undefined {
  if (authority?.kind === "agent") {
    return { origin: `http://127.0.0.1:${String(params.port)}`, path: agentSurfacePath };
  }
  if (authority?.kind === "cloud") {
    const origin = params.streamUrl === undefined ? undefined : apiOriginFrom(params.streamUrl);
    return origin === undefined ? undefined : { origin, path: cloudSurfacePath };
  }
  return undefined;
}

/** Modules from one origin's canvas router: what a host that is not this app passes in. */
export function cloudSurfaceCatalogue(origin: string): SurfaceCatalogue {
  return {
    moduleUrl: (activityId, surface) => `${origin}${cloudSurfacePath(activityId, surface)}`,
  };
}

let held: Authority | undefined;

/** Follow the authority that answered, so a cloud-only canvas fetches modules from the cloud. */
export function followSurfaceAuthority(authority: Authority | undefined): void {
  held = authority;
}

/** Modules from whichever authority this canvas is connected to right now. */
export function connectedSurfaceCatalogue(params: CanvasParams = readParams()): SurfaceCatalogue {
  return {
    moduleUrl: (activityId, surface) => {
      const base = surfaceModuleBase(held, params);
      return base === undefined ? "" : `${base.origin}${base.path(activityId, surface)}`;
    },
  };
}
